import type { CSSProperties } from 'react';
import type { ShellPresentation } from '../types/fieldMapping';

export function buildShellPresentation(source: {
  className?: string;
  style?: Record<string, string | number>;
}): ShellPresentation | undefined {
  const className = source.className?.trim();
  const style = source.style;
  const hasClassName = className != null && className.length > 0;
  const hasStyle = style != null && Object.keys(style).length > 0;

  if (!hasClassName && !hasStyle) return undefined;

  return {
    ...(hasClassName && { className }),
    ...(hasStyle && { style }),
  };
}

export interface MatrixFieldShellPresentationInput {
  shellPresentation?: ShellPresentation;
  alignStyle?: CSSProperties;
  debugStyle?: CSSProperties;
  debugClassName?: string;
  skipPresentation?: boolean;
}

/** Matches FormField LabelRow min-height — inline buttons pad by this when sharing a row with labeled fields. */
export const INLINE_ACTION_BUTTON_LABEL_ROW_PADDING_PX = 26;

/** Legacy default matrix shell for inline action-buttons beside labeled fields (unchanged unless orch overrides). */
export const DEFAULT_INLINE_ACTION_BUTTON_MATRIX_SHELL_STYLE: CSSProperties = {
  paddingTop: INLINE_ACTION_BUTTON_LABEL_ROW_PADDING_PX,
};

/**
 * Matrix cell styles for inline action-buttons sharing a row with labeled fields.
 * Applies {@link DEFAULT_INLINE_ACTION_BUTTON_MATRIX_SHELL_STYLE} first, then orch
 * `style` / `className` (via shellPresentation). Set orch `style.paddingTop`
 * (including `0`) to replace the default top padding only; other orch keys merge
 * on top without removing the default padding.
 */
export function buildInlineActionButtonMatrixShellStyle(input: {
  startNewRow?: boolean;
  shellPresentation?: ShellPresentation;
  hidden?: boolean;
}): CSSProperties | undefined {
  if (input.hidden) return undefined;

  const orchStyle = input.shellPresentation?.style as CSSProperties | undefined;
  const orchSetsPaddingTop =
    orchStyle != null &&
    Object.prototype.hasOwnProperty.call(orchStyle, 'paddingTop') &&
    orchStyle.paddingTop !== undefined;

  const useDefaultLabelPadding = input.startNewRow !== true && !orchSetsPaddingTop;

  if (!useDefaultLabelPadding && orchStyle == null) return undefined;

  return {
    ...(useDefaultLabelPadding ? DEFAULT_INLINE_ACTION_BUTTON_MATRIX_SHELL_STYLE : undefined),
    ...orchStyle,
  };
}

export function mergeMatrixFieldShellPresentation({
  shellPresentation,
  alignStyle,
  debugStyle,
  debugClassName,
  skipPresentation,
}: MatrixFieldShellPresentationInput): { style?: CSSProperties; className?: string } {
  const shellStyle = skipPresentation ? undefined : shellPresentation?.style;
  const mergedStyle =
    shellStyle || alignStyle || debugStyle
      ? {
          ...(shellStyle as CSSProperties | undefined),
          ...alignStyle,
          ...debugStyle,
        }
      : undefined;

  const shellClassName = skipPresentation ? undefined : shellPresentation?.className;
  const className = [shellClassName, debugClassName].filter(Boolean).join(' ') || undefined;

  return {
    ...(mergedStyle != null && { style: mergedStyle }),
    ...(className != null && { className }),
  };
}
